import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Grid,
  Stack,
  Typography,
  Box,
  Fade,
  Grow,
  alpha,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Paper,
  Button,
} from '@mui/material'
import { MaterialReactTable } from 'material-react-table';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import FinanceImage from "../../media/Lamp Poses-03-1.png"
import { useApi } from '../../hooks/useApi'
import RefreshIcon from '@mui/icons-material/Refresh';
import IconButton from '@mui/material/IconButton';

function Index () {

    const jApi = useApi();
    const theme = useTheme();
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [dattoData, setDattoData] = useState(null);
    const [sherwebData, setSherwebData] = useState(null);
    const [invoiceData, setInvoiceData] = useState(null);
    const [dattoStatus, setDattoStatus] = useState(null);
    const [sherwebStatus, setSherwebStatus] = useState(null);
    const [invoiceStatus, setInvoiceStatus] = useState(null);
    const [dattoCompleted, setDattoCompleted] = useState(null);
    const [sherwebCompleted, setSherwebCompleted] = useState(null);
    const [invoiceCompleted, setInvoiceCompleted] = useState(null);
    const [runningJob, setRunningJob] = useState(null);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            setErrorMessage(null);

            const today = new Date().toISOString().slice(0, 10);
            console.log('Fetching VES jobs for date:', today);
            const response = await jApi.request('GET', `ves/job?ymd=${today}`);
            console.log('VES response:', response.data);

            if (!response.data || response.data.length === 0) {
                setErrorMessage('No jobs found for today');
                return;
            }

            // Log all functionPaths to see what we're working with
            console.log('All job functionPaths:', response.data.map(job => job.functionpath));

            // Filter to only completed jobs and find the most recent for each type
            const completedJobs = response.data.filter(job => job.status === 'completed');

            const dattoJobs = completedJobs.filter(job =>
                job.functionpath && job.functionpath.includes('Get-ContractBalanceDatto')
            ).sort((a, b) => b.completed - a.completed);
            const dattoJob = dattoJobs[0];

            const sherwebJobs = completedJobs.filter(job =>
                job.functionpath && job.functionpath.includes('Get-ContractBalanceSherweb')
            ).sort((a, b) => b.completed - a.completed);
            const sherwebJob = sherwebJobs[0];

            const invoiceJobs = completedJobs.filter(job =>
                job.functionpath && job.functionpath.includes('Get-RecurringInvoiceVariance')
            ).sort((a, b) => b.completed - a.completed);
            const invoiceJob = invoiceJobs[0];

            // Check for any in-progress jobs
            const inProgressDatto = response.data.find(job =>
                job.functionpath && job.functionpath.includes('Get-ContractBalanceDatto') &&
                (job.status === 'queued' || job.status === 'processing' || job.status === 'running')
            );
            const inProgressSherweb = response.data.find(job =>
                job.functionpath && job.functionpath.includes('Get-ContractBalanceSherweb') &&
                (job.status === 'queued' || job.status === 'processing' || job.status === 'running')
            );
            const inProgressInvoice = response.data.find(job =>
                job.functionpath && job.functionpath.includes('Get-RecurringInvoiceVariance') &&
                (job.status === 'queued' || job.status === 'processing' || job.status === 'running')
            );

            setDattoStatus(inProgressDatto ? inProgressDatto.status : (dattoJob ? 'completed' : null));
            setSherwebStatus(inProgressSherweb ? inProgressSherweb.status : (sherwebJob ? 'completed' : null));
            setInvoiceStatus(inProgressInvoice ? inProgressInvoice.status : (invoiceJob ? 'completed' : null));

            console.log('Found jobs:', { dattoJob: !!dattoJob, sherwebJob: !!sherwebJob, invoiceJob: !!invoiceJob });

            if (dattoJob) {
                console.log('Fetching Datto job:', dattoJob.id);
                const dattoDetail = await jApi.request('GET', `ves/job?id=${dattoJob.id}`);
                console.log('Datto detail response:', dattoDetail);
                console.log('Datto detail.data:', dattoDetail.data);
                console.log('Datto results field:', dattoDetail.data.results);
                const parsedDatto = typeof dattoDetail.data.results === 'string'
                    ? JSON.parse(dattoDetail.data.results)
                    : dattoDetail.data.results;
                console.log('Parsed Datto data:', parsedDatto);
                setDattoData(parsedDatto);
                setDattoCompleted(dattoJob.completed);
                console.log('Set Datto data complete');
            }

            if (sherwebJob) {
                const sherwebDetail = await jApi.request('GET', `ves/job?id=${sherwebJob.id}`);
                const parsedSherweb = typeof sherwebDetail.data.results === 'string'
                    ? JSON.parse(sherwebDetail.data.results)
                    : sherwebDetail.data.results;
                console.log('Sherweb data:', parsedSherweb);
                setSherwebData(parsedSherweb);
                setSherwebCompleted(sherwebJob.completed);
            }

            if (invoiceJob) {
                const invoiceDetail = await jApi.request('GET', `ves/job?id=${invoiceJob.id}`);
                const parsedInvoice = typeof invoiceDetail.data.results === 'string'
                    ? JSON.parse(invoiceDetail.data.results)
                    : invoiceDetail.data.results;
                console.log('Invoice data:', parsedInvoice);
                setInvoiceData(parsedInvoice);
                setInvoiceCompleted(invoiceJob.completed);
            }

        } catch (ex) {
            console.error('Error fetching data:', ex);
            setErrorMessage(ex.toString());
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        // Auto-refresh every 30 seconds if there's a job in progress
        if (dattoStatus === 'queued' || dattoStatus === 'processing' || dattoStatus === 'running' ||
            sherwebStatus === 'queued' || sherwebStatus === 'processing' || sherwebStatus === 'running' ||
            invoiceStatus === 'queued' || invoiceStatus === 'processing' || invoiceStatus === 'running') {
            const interval = setInterval(fetchData, 30000);
            return () => clearInterval(interval);
        }
    }, [dattoStatus, sherwebStatus, invoiceStatus]);

    const runReport = async (reportType) => {
        try {
            setRunningJob(reportType);
            const functionPaths = {
                'datto': 'cma/report/finance/Get-ContractBalanceDatto.ps1',
                'sherweb': 'cma/report/finance/Get-ContractBalanceSherweb.ps1',
                'invoice': 'cma/report/finance/Get-RecurringInvoiceVariance.ps1'
            };

            await jApi.request('POST', 'ves/job', {
                functionpath: functionPaths[reportType],
                splat: {}
            });

            // Refresh data after submitting job
            setTimeout(fetchData, 2000);
        } catch (ex) {
            setErrorMessage(`Failed to queue ${reportType} report: ${ex.toString()}`);
        } finally {
            setRunningJob(null);
        }
    };

    const handleTabChange = (event, newValue) => {
        if (newValue !== null) {
            setActiveTab(newValue);
        }
    };

    // Gradient header style
    const headerGradient = {
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
      borderRadius: 3,
      border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
      position: 'relative',
      overflow: 'hidden',
      boxShadow: theme.palette.mode === 'dark'
        ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
        : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 100%)`,
        opacity: 0.5,
      },
    };


    return (
        <Box sx={{
          minHeight: '100vh',
        }}>
        <Container maxWidth="xl">
            <Fade in timeout={500}>
              <Box
                sx={{
                  pt: 3,
                  pb: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    background: alpha(theme.palette.background.paper, 0.6),
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                    },
                  }}
                >
                  <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1,
                        py: 0.5,
                        borderRadius: 1.5,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: alpha(theme.palette.secondary.main, 0.1),
                        },
                      }}
                    >
                      <HomeRoundedIcon
                        sx={{
                          fontSize: 20,
                          color: theme.palette.secondary.main,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.secondary.main,
                          fontWeight: 600,
                        }}
                      >
                        Home
                      </Typography>
                    </Box>
                  </Link>
                  <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>
                    /
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: 600,
                      px: 1,
                      py: 0.5,
                    }}
                  >
                    Finance Center
                  </Typography>
                </Box>
              </Box>
            </Fade>

            <Grid container spacing={2}>
              {/* Compact Header */}
              <Grid size={12}>
                <Grow in timeout={700}>
                  <Box sx={headerGradient}>
                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{
                        p: 2,
                        alignItems: 'center',
                        position: 'relative',
                        zIndex: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: 2,
                          overflow: 'hidden',
                          boxShadow: `0 4px 8px ${alpha(theme.palette.common.black, 0.2)}`,
                          border: `2px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                        }}
                      >
                        <img width="100%" height="100%" src={FinanceImage} alt="Logo" style={{ objectFit: 'cover' }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                          }}
                        >
                          Finance Center
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Making cents of your dollars!
                        </Typography>
                      </Box>
                      <IconButton
                        onClick={fetchData}
                        disabled={isLoading}
                        size="small"
                        sx={{
                          color: theme.palette.secondary.main,
                          '&:hover': {
                            background: alpha(theme.palette.secondary.main, 0.1),
                          },
                        }}
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Stack>
                    {isLoading && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }} />}
                  </Box>
                </Grow>
              </Grid>

              {errorMessage && (
                <Grid size={12}>
                  <Fade in>
                    <Paper
                      sx={{
                        p: 2,
                        background: alpha(theme.palette.error.main, 0.1),
                        border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                        borderRadius: 2,
                      }}
                    >
                      <Typography variant="body1" color="error.main">
                        {errorMessage}
                      </Typography>
                    </Paper>
                  </Fade>
                </Grid>
              )}

              {/* Toggle Button Navigation */}
              <Grid size={12}>
                <Grow in timeout={900}>
                  <Box
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      background: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.background.default, 0.9)
                        : alpha(theme.palette.background.default, 0.95),
                      backdropFilter: 'blur(10px)',
                      pb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                        gap: 2,
                      }}
                    >
                      <ToggleButtonGroup
                        value={activeTab}
                        exclusive
                        onChange={handleTabChange}
                        sx={{
                          background: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.background.paper, 0.6)
                            : alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(10px)',
                          border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
                          borderRadius: 3,
                          p: 0.5,
                          '& .MuiToggleButton-root': {
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 500,
                            px: 3,
                            py: 1.5,
                            border: 'none',
                            borderRadius: 2,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              background: alpha(theme.palette.secondary.main, 0.1),
                            },
                            '&.Mui-selected': {
                              background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                              color: theme.palette.secondary.contrastText,
                              boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.3)}`,
                              '&:hover': {
                                background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                              },
                            },
                          },
                        }}
                      >
                        <ToggleButton value={0}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <span>Datto Balance</span>
                            {dattoStatus === 'queued' && <Chip label="Queued" size="small" color="info" />}
                            {dattoStatus === 'processing' && <Chip label="Processing" size="small" color="warning" />}
                            {dattoStatus === 'running' && <Chip label="Running" size="small" color="warning" />}
                          </Stack>
                        </ToggleButton>
                        <ToggleButton value={1}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <span>Sherweb Balance</span>
                            {sherwebStatus === 'queued' && <Chip label="Queued" size="small" color="info" />}
                            {sherwebStatus === 'processing' && <Chip label="Processing" size="small" color="warning" />}
                            {sherwebStatus === 'running' && <Chip label="Running" size="small" color="warning" />}
                          </Stack>
                        </ToggleButton>
                        <ToggleButton value={2}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <span>Invoice Variance</span>
                            {invoiceStatus === 'queued' && <Chip label="Queued" size="small" color="info" />}
                            {invoiceStatus === 'processing' && <Chip label="Processing" size="small" color="warning" />}
                            {invoiceStatus === 'running' && <Chip label="Running" size="small" color="warning" />}
                          </Stack>
                        </ToggleButton>
                      </ToggleButtonGroup>

                      {/* Run Report Button - Right Justified */}
                      {activeTab === 0 && (
                        <Button
                          onClick={() => runReport('datto')}
                          disabled={runningJob === 'datto' || dattoStatus === 'queued' || dattoStatus === 'processing' || dattoStatus === 'running'}
                          startIcon={<RefreshIcon />}
                          variant="contained"
                          sx={{
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            '&:hover': {
                              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                            },
                          }}
                        >
                          {dattoStatus === 'queued' || dattoStatus === 'processing' || dattoStatus === 'running' ? 'Report Processing...' : 'Run Datto Balance Report'}
                        </Button>
                      )}
                      {activeTab === 1 && (
                        <Button
                          onClick={() => runReport('sherweb')}
                          disabled={runningJob === 'sherweb' || sherwebStatus === 'queued' || sherwebStatus === 'processing' || sherwebStatus === 'running'}
                          startIcon={<RefreshIcon />}
                          variant="contained"
                          sx={{
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            '&:hover': {
                              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                            },
                          }}
                        >
                          {sherwebStatus === 'queued' || sherwebStatus === 'processing' || sherwebStatus === 'running' ? 'Report Processing...' : 'Run Sherweb Balance Report'}
                        </Button>
                      )}
                      {activeTab === 2 && (
                        <Button
                          onClick={() => runReport('invoice')}
                          disabled={runningJob === 'invoice' || invoiceStatus === 'queued' || invoiceStatus === 'processing' || invoiceStatus === 'running'}
                          startIcon={<RefreshIcon />}
                          variant="contained"
                          sx={{
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            '&:hover': {
                              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                            },
                          }}
                        >
                          {invoiceStatus === 'queued' || invoiceStatus === 'processing' || invoiceStatus === 'running' ? 'Report Processing...' : 'Run Invoice Variance Report'}
                        </Button>
                      )}
                    </Box>

                    <Paper
                      sx={{
                        background: theme.palette.mode === 'dark'
                          ? alpha(theme.palette.background.paper, 0.8)
                          : alpha(theme.palette.background.paper, 0.95),
                        backdropFilter: 'blur(20px)',
                        border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
                        borderRadius: 3,
                        p: 2,
                      }}
                    >
                      {activeTab === 0 && <DattoBalancePanel data={dattoData} theme={theme} isLoading={isLoading} completedTime={dattoCompleted} />}
                      {activeTab === 1 && <SherwebBalancePanel data={sherwebData} theme={theme} isLoading={isLoading} completedTime={sherwebCompleted} />}
                      {activeTab === 2 && <InvoiceVariancePanel data={invoiceData} theme={theme} isLoading={isLoading} completedTime={invoiceCompleted} />}
                    </Paper>
                  </Box>
                </Grow>
              </Grid>

            </Grid>
          </Container>
        </Box>
    )
}

// Panel Components
function DattoBalancePanel({ data, theme, isLoading, completedTime }) {
  if (isLoading) return <Typography>Loading...</Typography>;
  if (!data) return <Typography color="text.secondary">No Datto balance data available for today.</Typography>;

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const columns = [
    { accessorKey: 'customer_code', header: 'Code', size: 100 },
    { accessorKey: 'customer_name', header: 'Customer', size: 200 },
    { accessorKey: 'contract_name', header: 'Contract', size: 180 },
    { accessorKey: 'service_type', header: 'Service', size: 120 },
    { accessorKey: 'contract_units', header: 'Contract', size: 100 },
    { accessorKey: 'actual_devices', header: 'Actual', size: 100 },
    {
      accessorKey: 'variance',
      header: 'Variance',
      size: 100,
      Cell: ({ cell }) => (
        <Chip
          label={cell.getValue()}
          size="small"
          color={cell.getValue() < 0 ? 'error' : cell.getValue() > 0 ? 'warning' : 'success'}
        />
      ),
    },
  ];

  return (
    <>
      {completedTime && (
        <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Chip
            label={`Data as of: ${formatTimestamp(completedTime)}`}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ fontSize: '0.75rem' }}
          />
        </Box>
      )}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Card sx={{ flex: 1, background: alpha(theme.palette.info.main, 0.1) }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h6" sx={{ fontSize: '1.25rem' }}>{data.summary?.total_service_lines || 0}</Typography>
            <Typography variant="caption" color="text.secondary">Total Lines</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, background: alpha(theme.palette.error.main, 0.1) }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h6" sx={{ fontSize: '1.25rem' }}>{data.summary?.mismatched_lines || 0}</Typography>
            <Typography variant="caption" color="text.secondary">Mismatched</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, background: alpha(theme.palette.success.main, 0.1) }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h6" sx={{ fontSize: '1.25rem' }}>{data.summary?.balanced_lines || 0}</Typography>
            <Typography variant="caption" color="text.secondary">Balanced</Typography>
          </CardContent>
        </Card>
      </Stack>
      <MaterialReactTable
        key={`datto-${data.mismatches?.length || 0}`}
        columns={columns}
        data={data.mismatches || []}
        enableDensityToggle={false}
        renderTopToolbarCustomActions={({ table }) => (
          <Button
            onClick={() => {
              // Use all data, not just current page
              const allRows = data.mismatches || [];
              const csv = allRows.map(row =>
                columns.map(col => {
                  const value = row[col.accessorKey];
                  // Escape values that contain commas or quotes
                  const stringValue = value?.toString() || '';
                  return stringValue.includes(',') || stringValue.includes('"')
                    ? `"${stringValue.replace(/"/g, '""')}"`
                    : stringValue;
                }).join(',')
              );
              const header = columns.map(col => col.header).join(',');
              const csvContent = [header, ...csv].join('\n');
              const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `datto-balance-${new Date().toISOString().split('T')[0]}.csv`;
              a.click();
              window.URL.revokeObjectURL(url);
            }}
            variant="contained"
            size="small"
            sx={{ ml: 2 }}
          >
            Export to Excel
          </Button>
        )}
        initialState={{ density: 'compact', pagination: { pageIndex: 0, pageSize: 25 } }}
        muiTableContainerProps={{ sx: { maxHeight: '60vh' } }}
      />
    </>
  );
}

function SherwebBalancePanel({ data, theme, isLoading, completedTime }) {
  if (isLoading) return <Typography>Loading...</Typography>;
  if (!data) return <Typography color="text.secondary">No Sherweb balance data available for today.</Typography>;

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const columns = [
    { accessorKey: 'customer_code', header: 'Code', size: 70 },
    { accessorKey: 'customer_name', header: 'Customer', size: 140 },
    { accessorKey: 'contract_name', header: 'Contract', size: 120 },
    { accessorKey: 'contract_term', header: 'Term', size: 70 },
    { accessorKey: 'service_description', header: 'Service', size: 140 },
    { accessorKey: 'service_sku', header: 'SKU', size: 100 },
    { accessorKey: 'contract_units', header: 'Units', size: 70 },
    { accessorKey: 'sherweb_quantity', header: 'Sherweb', size: 70 },
    {
      accessorKey: 'variance',
      header: 'Variance',
      size: 90,
      Cell: ({ cell }) => (
        <Chip
          label={cell.getValue()}
          size="small"
          color={cell.getValue() < 0 ? 'error' : cell.getValue() > 0 ? 'warning' : 'success'}
        />
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 150,
      Cell: ({ cell }) => {
        const status = cell.getValue();
        let color = 'default';
        if (status === 'MISMATCH') color = 'error';
        else if (status === 'SHERWEB_ONLY') color = 'warning';
        else if (status === 'CONTRACT_ONLY') color = 'info';
        else if (status === 'NO_SKU_IN_AUTOTASK') color = 'warning';
        else if (status === 'NO_SHERWEB_SUBSCRIPTIONS') color = 'secondary';
        else if (status === 'NO_SHERWEB_CUSTOMER') color = 'secondary';

        return (
          <Chip
            label={status?.replace(/_/g, ' ')}
            size="small"
            color={color}
          />
        );
      },
    },
  ];

  return (
    <>
      {completedTime && (
        <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Chip
            label={`Data as of: ${formatTimestamp(completedTime)}`}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ fontSize: '0.75rem' }}
          />
        </Box>
      )}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Card sx={{ flex: 1, background: alpha(theme.palette.info.main, 0.1) }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h6" sx={{ fontSize: '1.25rem' }}>{data.summary?.total_service_lines || 0}</Typography>
            <Typography variant="caption" color="text.secondary">Total Lines</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, background: alpha(theme.palette.success.main, 0.1) }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h6" sx={{ fontSize: '1.25rem' }}>{data.summary?.balanced_lines || 0}</Typography>
            <Typography variant="caption" color="text.secondary">Balanced</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, background: alpha(theme.palette.error.main, 0.1) }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h6" sx={{ fontSize: '1.25rem' }}>{data.summary?.mismatched_lines || 0}</Typography>
            <Typography variant="caption" color="text.secondary">Mismatched</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, background: alpha(theme.palette.warning.main, 0.1) }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h6" sx={{ fontSize: '1.25rem' }}>{data.summary?.sherweb_only_lines || 0}</Typography>
            <Typography variant="caption" color="text.secondary">Sherweb Only</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, background: alpha(theme.palette.secondary.main, 0.1) }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h6" sx={{ fontSize: '1.25rem' }}>{data.summary?.contract_only_lines || 0}</Typography>
            <Typography variant="caption" color="text.secondary">Contract Only</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, background: alpha(theme.palette.warning.main, 0.1) }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h6" sx={{ fontSize: '1.25rem' }}>{data.summary?.no_sku_in_autotask || 0}</Typography>
            <Typography variant="caption" color="text.secondary">No SKU</Typography>
          </CardContent>
        </Card>
      </Stack>
      <MaterialReactTable
        key={`sherweb-${data.mismatches?.length || 0}`}
        columns={columns}
        data={data.mismatches || []}
        enableColumnResizing
        enableDensityToggle={false}
        columnResizeMode="onChange"
        layoutMode="grid"
        enableRowSelection={false}
        renderTopToolbarCustomActions={({ table }) => (
          <Button
            onClick={() => {
              // Use all data, not just current page
              const allRows = data.mismatches || [];
              const csv = allRows.map(row =>
                columns.map(col => {
                  const value = row[col.accessorKey];
                  // Escape values that contain commas or quotes
                  const stringValue = value?.toString() || '';
                  return stringValue.includes(',') || stringValue.includes('"')
                    ? `"${stringValue.replace(/"/g, '""')}"`
                    : stringValue;
                }).join(',')
              );
              const header = columns.map(col => col.header).join(',');
              const csvContent = [header, ...csv].join('\n');
              const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `sherweb-balance-${new Date().toISOString().split('T')[0]}.csv`;
              a.click();
              window.URL.revokeObjectURL(url);
            }}
            variant="contained"
            size="small"
            sx={{ ml: 2 }}
          >
            Export to Excel
          </Button>
        )}
        positionToolbarAlertBanner="bottom"
        initialState={{ density: 'compact', pagination: { pageIndex: 0, pageSize: 25 } }}
        muiTableContainerProps={{ sx: { maxHeight: '60vh' } }}
      />
    </>
  );
}

function InvoiceVariancePanel({ data, theme, isLoading, completedTime }) {
  if (isLoading) return <Typography>Loading...</Typography>;
  if (!data) return <Typography color="text.secondary">No invoice variance data available for today.</Typography>;

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const columns = [
    { accessorKey: 'customer_code', header: 'Code', size: 70 },
    { accessorKey: 'customer_name', header: 'Customer', size: 140 },
    { accessorKey: 'contract_name', header: 'Contract', size: 120 },
    { accessorKey: 'recent_invoice_id', header: 'Recent ID', size: 80 },
    { accessorKey: 'recent_invoice_date', header: 'Recent Date', size: 100 },
    {
      accessorKey: 'recent_invoice_amount',
      header: 'Recent $',
      size: 90,
      Cell: ({ cell }) => `$${cell.getValue()?.toFixed(2) || '0.00'}`,
    },
    { accessorKey: 'previous_invoice_id', header: 'Prev ID', size: 80 },
    { accessorKey: 'previous_invoice_date', header: 'Prev Date', size: 100 },
    {
      accessorKey: 'previous_invoice_amount',
      header: 'Prev $',
      size: 90,
      Cell: ({ cell }) => `$${cell.getValue()?.toFixed(2) || '0.00'}`,
    },
    {
      accessorKey: 'variance_dollar',
      header: 'Var $',
      size: 90,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        return (
          <span style={{ color: value < 0 ? '#d32f2f' : value > 0 ? '#ed6c02' : '#2e7d32' }}>
            ${value?.toFixed(2) || '0.00'}
          </span>
        );
      },
    },
    {
      accessorKey: 'variance_percent',
      header: 'Var %',
      size: 90,
      Cell: ({ cell }) => (
        <Chip
          label={`${cell.getValue()?.toFixed(1) || 0}%`}
          size="small"
          color={Math.abs(cell.getValue()) > 20 ? 'error' : 'warning'}
        />
      ),
    },
    {
      accessorKey: 'category',
      header: 'Category',
      size: 100,
      Cell: ({ cell }) => (
        <Chip
          label={cell.getValue()}
          size="small"
          color={cell.getValue() === 'MAJOR' ? 'error' : 'warning'}
        />
      ),
    },
  ];

  return (
    <>
      {completedTime && (
        <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Chip
            label={`Data as of: ${formatTimestamp(completedTime)}`}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ fontSize: '0.75rem' }}
          />
        </Box>
      )}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Card sx={{ flex: 1, background: alpha(theme.palette.info.main, 0.1) }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h6" sx={{ fontSize: '1.25rem' }}>{data.summary?.total_contracts || 0}</Typography>
            <Typography variant="caption" color="text.secondary">Total Contracts</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, background: alpha(theme.palette.success.main, 0.1) }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h6" sx={{ fontSize: '1.25rem' }}>{data.summary?.contracts_analyzed || 0}</Typography>
            <Typography variant="caption" color="text.secondary">Analyzed</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, background: alpha(theme.palette.success.main, 0.1) }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h6" sx={{ fontSize: '1.25rem' }}>{data.summary?.stable_contracts || 0}</Typography>
            <Typography variant="caption" color="text.secondary">Stable</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, background: alpha(theme.palette.secondary.main, 0.1) }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h6" sx={{ fontSize: '1.25rem' }}>{data.summary?.minor_variance || 0}</Typography>
            <Typography variant="caption" color="text.secondary">Minor</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, background: alpha(theme.palette.warning.main, 0.1) }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h6" sx={{ fontSize: '1.25rem' }}>{data.summary?.significant_variance || 0}</Typography>
            <Typography variant="caption" color="text.secondary">Significant</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, background: alpha(theme.palette.error.main, 0.1) }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h6" sx={{ fontSize: '1.25rem' }}>{data.summary?.major_variance || 0}</Typography>
            <Typography variant="caption" color="text.secondary">Major</Typography>
          </CardContent>
        </Card>
      </Stack>
      <MaterialReactTable
        key={`invoice-${data.flagged?.length || 0}`}
        columns={columns}
        data={data.flagged || []}
        enableColumnResizing
        enableDensityToggle={false}
        columnResizeMode="onChange"
        layoutMode="grid"
        renderTopToolbarCustomActions={({ table }) => (
          <Button
            onClick={() => {
              // Use all data, not just current page
              const allRows = data.flagged || [];
              const csv = allRows.map(row =>
                columns.map(col => {
                  const value = row[col.accessorKey];
                  // Escape values that contain commas or quotes
                  const stringValue = value?.toString() || '';
                  return stringValue.includes(',') || stringValue.includes('"')
                    ? `"${stringValue.replace(/"/g, '""')}"`
                    : stringValue;
                }).join(',')
              );
              const header = columns.map(col => col.header).join(',');
              const csvContent = [header, ...csv].join('\n');
              const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `invoice-variance-${new Date().toISOString().split('T')[0]}.csv`;
              a.click();
              window.URL.revokeObjectURL(url);
            }}
            variant="contained"
            size="small"
            sx={{ ml: 2 }}
          >
            Export to Excel
          </Button>
        )}
        initialState={{ density: 'compact', pagination: { pageIndex: 0, pageSize: 25 } }}
        muiTableContainerProps={{ sx: { maxHeight: '60vh' } }}
      />
    </>
  );
}

export default Index